import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import { useContext, useRef, useState, forwardRef, useMemo } from 'react';
import {
    calculateContainerHeight,
    calculateFontSize,
    fetchCurrency,
    formatNumWithKMBAlt,
    getConfig,
    scaleMaxValue,
} from 'utils/helperMethods';
// import { profitPoolData } from './data/profirPoolData';
// import { extendedProfitPoolData } from './data/extendedProfitPoolData';
import StackedBarChart from 'components/molecules/StackedBarChart';
import { UserContext } from 'contexts/ConfigContext';
import { groupBarChartWidth } from 'components/organism/GrowthAnalysis/BrandValueChartContainer/BrandValueChartContainer';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { calculateChartWidth } from 'components/organism/BumpReport/BumpChartContainer';
import Loader from 'components/molecules/Loader';
import useProfitPoolbyHierarchyDataInfiniteScroll from './hooks/useProfitPoolbyHierarchyDataInfiniteScroll';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { FilterContextData } from 'contexts/FilterContext';
import { isEmpty } from 'lodash';

const ProfitPoolbyHierarchyChart = forwardRef<HTMLElement, {}>((props, ref) => {
    const chartHeight = calculateContainerHeight(3);
    const [toLeft, setToLeft] = useState(0);
    const chartRef = useRef<any>(null);
    const { appConfig } = useContext(UserContext);
    const currency = fetchCurrency(appConfig) || '';
    const color2 = ['#8EDFEE', '#4557D5', '#9BDE91', '#293BB8'];

    const {
        data: stackedBarChartDataInfiniteScroll,
        tableData: profitPoolTableData,
        min: minInfiniteScroll,
        max: maxInfiniteScroll,
        isPending: isPendingInfiniteScroll,
        isError: isErrorInfiniteScroll,
        isSuccess: isSuccessInfiniteScroll,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useProfitPoolbyHierarchyDataInfiniteScroll();

    const minBound = useMemo(() => {
        return minInfiniteScroll < 0 ? minInfiniteScroll : 0;
    }, [minInfiniteScroll]);

    const { multiSelectedTiers, profitPoolTierList, idForRestOfComponent, allDimAndAttributeTypeList } =
        useContext(FilterContextData);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { tiersColorMapPPOverview, kpiLabelMapping, defaultCurrencySymbol } = profitPoolConfig;

    // WIdth Calculation
    const yAxisGraphWidth = calculateFontSize(window.innerWidth, 90);
    const containerWidth2 = useContainerWidth({
        containerId: 'growth-analysis-sales-infinite-scroll-profit',
    });
    const graphViewContainerWidth2 = Math.max(16 * groupBarChartWidth, containerWidth2);
    const graphViewContainerWidth3 = Math.max(16 * groupBarChartWidth, containerWidth2) + 300;

    // const perGraphComponentWidth = graphViewContainerWidth3 / stackedBarChartDataInfiniteScroll?.length || 0;

    const { updatedBarWidth, updatedWidth } = calculateChartWidth(chartRef.current?.offsetWidth, 26);

    // Create a mapping from attributeDesc (description) to attribute_ex_id (ID) from chart data
    // and a lookup map for tier percentages: attributeDesc + tier -> percentage
    const { descToIdMap, tierPercentLookup } = useMemo(() => {
        const descToId: Record<string, string> = {};
        const lookup: Record<string, string> = {};

        // Create mapping from description to ID using chart data
        if (stackedBarChartDataInfiniteScroll) {
            stackedBarChartDataInfiniteScroll.forEach((item: any) => {
                if (item.attributeDesc && item.attribute_ex_id) {
                    descToId[item.attributeDesc] = item.attribute_ex_id;
                }
            });
        }

        // Create lookup map using ID (which is what tableData uses)
        if (profitPoolTableData && multiSelectedTiers) {
            profitPoolTableData.forEach((row: any) => {
                multiSelectedTiers.forEach((tier: string) => {
                    const key = `${row.attributeDesc}_${tier}`;
                    const percentKey = `${tier}Percent`;
                    if (row[percentKey]) {
                        lookup[key] = row[percentKey];
                    }
                });
            });
        }

        return { descToIdMap: descToId, tierPercentLookup: lookup };
    }, [profitPoolTableData, multiSelectedTiers, stackedBarChartDataInfiniteScroll]);

    if (isPendingInfiniteScroll)
        return (
            <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Loader />
            </div>
        );

    if (isErrorInfiniteScroll) return <ErrorBox />;

    if (isSuccessInfiniteScroll && isEmpty(stackedBarChartDataInfiniteScroll)) {
        return (
            <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <ErrorBox msg={'No Data'} />
            </div>
        );
    }

    return (
        <>
            <div className="items-center flex flex-row justify-between my-2 mb-4">
                {true ? (
                    <>
                        <div className="text-sm">
                            <p>{`Total ${kpiLabelMapping['profit']} ${defaultCurrencySymbol}`}</p>
                        </div>
                        <div className="flex flex-row gap-2 text-xs">
                            {multiSelectedTiers.map((tier) => {
                                return (
                                    <>
                                        <div
                                            className="w-3 h-2 self-center"
                                            style={{ backgroundColor: tiersColorMapPPOverview?.[tier] }}
                                        />
                                        <p>{profitPoolTierList.find((t) => t.tierReference === tier)?.tierName}</p>
                                    </>
                                );
                            })}
                        </div>
                        <div />
                    </>
                ) : null}
            </div>
            <section ref={ref}>
                <div className="flex">
                    <div className="">
                        <Barchart
                            padding={[0, 0, 15, 0]}
                            barWidth={0}
                            xAxisConfig={false}
                            dataset={[]}
                            height={chartHeight}
                            loading={false}
                            chartContainer={`bumpChart-main2`}
                            fields={['attributeDesc', 'abs_profit', 'tier']}
                            minBound={minBound}
                            maxBound={scaleMaxValue(30, maxInfiniteScroll)}
                            width={60}
                            colorKey={'tier'}
                            YGrid={false}
                            yAxisConfig={true}
                            yAxisLabelFormatter={(val: any) => {
                                return `${defaultCurrencySymbol} ${formatNumWithKMBAlt(val)}`;
                            }}
                        />
                    </div>
                    <HorizontalInfiniteScroller
                        id={`growth-analysis-sales-infinite-scroll-profit`}
                        hasMore={hasNextPage}
                        scrollThreshold={0.9}
                        toScrollWidth={toLeft}
                        onNext={(width: number) => {
                            !isFetchingNextPage && fetchNextPage();
                            // width > 0 && setToLeft(toLeft + width);
                        }}
                    >
                        <div
                            id="growth-analysis-sales-infinite-scroll-profit"
                            ref={chartRef}
                            className="flex pb-[60px] overflow-x-auto max-w-full"
                        >
                            <StackedBarChart
                                barWidth={40}
                                dataset={stackedBarChartDataInfiniteScroll || []}
                                height={chartHeight}
                                loading={isFetchingNextPage}
                                showLoader={false}
                                intervalLabelKey="totalVal"
                                intervalFormatter={(val: any) => {
                                    return `${defaultCurrencySymbol}${formatNumWithKMBAlt(val, 2, 2)}`;
                                }}
                                chartContainer={`bumpChart-main`}
                                fields={['attributeDesc', 'abs_profit', 'tier', 'totalVal', 'index']}
                                minBound={minBound}
                                maxBound={scaleMaxValue(30, maxInfiniteScroll)}
                                width={Math.max(
                                    graphViewContainerWidth3 * (stackedBarChartDataInfiniteScroll?.length / 30 || 1),
                                    graphViewContainerWidth3,
                                )}
                                xAxisLabelFormatter={(val: any, item: any, index: number) => {
                                    return val;
                                }}
                                xLabelFontSize={11}
                                colorKey={'tier'}
                                groupedChartColor={color2 as [string, string]}
                                currency={currency}
                                YGrid={true}
                                tooltipFormatter={(xAxis: any, val: any, attributeDesc?: any) => {
                                    const tierName = profitPoolTierList.find(
                                        (tier) => tier.tierReference === xAxis,
                                    )?.tierName;
                                    // Convert description to ID for lookup (tableData uses ID as key)
                                    const attributeId =
                                        attributeDesc && descToIdMap[attributeDesc]
                                            ? descToIdMap[attributeDesc]
                                            : attributeDesc;
                                    const lookupKey = attributeId ? `${attributeId}_${xAxis}` : null;
                                    const tierPercent =
                                        lookupKey && tierPercentLookup[lookupKey] ? tierPercentLookup[lookupKey] : null;
                                    return {
                                        name: tierName,
                                        value: tierPercent
                                            ? `${defaultCurrencySymbol}${formatNumWithKMBAlt(
                                                  val,
                                                  2,
                                                  2,
                                              )} (${tierPercent})`
                                            : `${defaultCurrencySymbol}${formatNumWithKMBAlt(val, 2, 2)}`,
                                    };
                                }}
                                disableTotalLabelYOffset={true}
                                toolTipFields={`tier*abs_profit*attributeDesc`}
                            />
                        </div>
                    </HorizontalInfiniteScroller>
                </div>
            </section>
        </>
    );
});

export default ProfitPoolbyHierarchyChart;
